import { Component, inject, signal, computed } from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { filter, interval } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '../../auth/auth.service';
import { LogoutReason } from '@shared/constants';
import { SessionManagementService } from '../session-management.service';

@Component({
  selector: 'pulse-session-timeout-dialog',
  imports: [ButtonModule, DialogModule, ProgressBarModule],
  templateUrl: './session-timeout-dialog.component.html',
  styleUrl: './session-timeout-dialog.component.scss'
})
export class SessionTimeoutDialogComponent {
  private authService = inject(AuthService);
  private sessionManagementService = inject(SessionManagementService);
  private _timeRemaining = signal(0);
  private _maxTime = signal(300); // 5 minutes in seconds
  private _isVisible = signal(false);

  isVisible = computed(() => this._isVisible());
  timeRemaining = computed(() => this._timeRemaining());

  progressValue = computed(() => {
    const remaining = this.timeRemaining();
    const max = this._maxTime();
    return max > 0 ? ((max - remaining) / max) * 100 : 0;
  });

  constructor() {
    this.initializeDialog();
  }

  private initializeDialog(): void {
    toObservable(this.sessionManagementService.showTimeoutWarning)
      .pipe(
        filter((showTimeoutWarning: boolean) => showTimeoutWarning),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.showDialog();
      });

    // Update countdown every second when dialog is visible
    interval(1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.isVisible()) {
          const currentTime = this.timeRemaining();
          if (currentTime > 0) {
            this._timeRemaining.set(currentTime - 1);
          } else {
            this.logout();
          }
        }
      });
  }

  private showDialog(): void {
    // Get the current time remaining from the session management service
    const timeRemaining = this.sessionManagementService.timeRemaining();
    const timeRemainingSeconds = Math.round(timeRemaining);

    this._timeRemaining.set(timeRemainingSeconds);
    this._maxTime.set(timeRemainingSeconds); // Set max time to the initial warning time
    this._isVisible.set(true);
  }

  private hideDialog(): void {
    this._timeRemaining.set(0);
    this._maxTime.set(0);
    this._isVisible.set(false);
  }

  public extendSession(): void {
    this.sessionManagementService.extendSession();
    this.hideDialog();
  }

  public logout(): void {
    this.authService.logout(LogoutReason.SessionExpired);
    this.hideDialog();
  }

  public formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}
